import { action, makeObservable, observable } from "mobx"

export const MCP_SERVERS_NAMESPACE = "wildlark.mcpServers"
export const MCP_SERVERS_KEY = "servers.v1"

/**
 * OAuth tokens received after successful authentication.
 * Obtained automatically via OAuth discovery and dynamic client registration.
 */
export interface McpOAuthTokens {
    accessToken: string
    refreshToken?: string
    expiresAt?: string // ISO timestamp
    tokenType: string
}

/**
 * Health status for MCP servers.
 */
export type McpHealthStatus = "unknown" | "healthy" | "unhealthy" | "needs_auth"

/**
 * Base fields shared by all MCP server types.
 */
interface McpServerBase {
    id: string
    name: string
    enabled: boolean
    presetId?: string // Which preset it was created from (stripe, github, etc.)
    lastTested?: string // ISO timestamp
    healthStatus: McpHealthStatus
    createdAt: string
    updatedAt: string
}

/**
 * HTTP MCP server configuration.
 * OAuth endpoints are discovered automatically from the server URL per MCP spec.
 */
export interface McpHttpServerItem extends McpServerBase {
    transportType: "http"
    url: string
    headers?: Record<string, string> // Custom headers (Authorization is added automatically from oauthTokens)
    oauthTokens?: McpOAuthTokens
}

/**
 * Stdio MCP server configuration (local process).
 */
export interface McpStdioServerItem extends McpServerBase {
    transportType: "stdio"
    command: string
    args?: string[]
    envVars?: Record<string, string>
}

/**
 * MCP server configuration - discriminated union.
 * Use `server.transportType` to narrow the type.
 */
export type McpServerItem = McpHttpServerItem | McpStdioServerItem

/**
 * Update payload for MCP servers.
 * Allows updating any field that's valid for either HTTP or Stdio servers.
 * The transportType cannot be changed after creation.
 */
export interface McpServerUpdate {
    name?: string
    enabled?: boolean
    healthStatus?: McpHealthStatus
    lastTested?: string
    // HTTP fields
    url?: string
    headers?: Record<string, string>
    oauthTokens?: McpOAuthTokens
    // Stdio fields
    command?: string
    args?: string[]
    envVars?: Record<string, string>
}

export interface McpServerRecord {
    v: 1
    servers: McpServerItem[]
    updatedAt: string
}

export interface McpServerArrayHandle<T extends { id: string }> {
    all(): T[]
    get(id: string): T | undefined
    push(item: T): void
    update(id: string, recipe: (draft: T) => void): void
    delete(id: string): void
}

/**
 * McpServerStore manages all MCP server configurations.
 */
export interface McpServerStore {
    servers: McpServerArrayHandle<McpServerItem>
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

function stringField(record: Record<string, unknown>, key: string): string | undefined {
    const value = record[key]
    return typeof value === "string" && value ? value : undefined
}

function booleanField(record: Record<string, unknown>, key: string): boolean | undefined {
    const value = record[key]
    return typeof value === "boolean" ? value : undefined
}

function stringRecord(value: unknown): Record<string, string> | undefined {
    if (!isRecord(value)) return undefined
    const result: Record<string, string> = {}
    for (const [key, item] of Object.entries(value)) {
        if (typeof item === "string") result[key] = item
    }
    return Object.keys(result).length > 0 ? result : undefined
}

function stringArray(value: unknown): string[] | undefined {
    if (!Array.isArray(value)) return undefined
    const result = value.filter((item) => typeof item === "string")
    return result.length > 0 ? result : undefined
}

function healthStatus(value: unknown): McpHealthStatus {
    return value === "healthy" || value === "unhealthy" || value === "needs_auth" ? value : "unknown"
}

function parseOAuthTokens(value: unknown): McpOAuthTokens | undefined {
    if (!isRecord(value)) return undefined
    const accessToken = stringField(value, "accessToken")
    const tokenType = stringField(value, "tokenType")
    if (!accessToken || !tokenType) return undefined
    return {
        accessToken,
        tokenType,
        refreshToken: stringField(value, "refreshToken"),
        expiresAt: stringField(value, "expiresAt"),
    }
}

export function parseMcpServerItem(value: unknown): McpServerItem | null {
    if (!isRecord(value)) return null
    const id = stringField(value, "id")
    const name = stringField(value, "name")
    const createdAt = stringField(value, "createdAt")
    const updatedAt = stringField(value, "updatedAt")
    const enabled = booleanField(value, "enabled")
    if (!id || !name || !createdAt || !updatedAt || enabled === undefined) return null

    const base = {
        id,
        name,
        enabled,
        presetId: stringField(value, "presetId"),
        lastTested: stringField(value, "lastTested"),
        healthStatus: healthStatus(value.healthStatus),
        createdAt,
        updatedAt,
    }

    if (value.transportType === "http") {
        const url = stringField(value, "url")
        if (!url) return null
        return {
            ...base,
            transportType: "http",
            url,
            headers: stringRecord(value.headers),
            oauthTokens: parseOAuthTokens(value.oauthTokens),
        }
    }

    if (value.transportType === "stdio") {
        const command = stringField(value, "command")
        if (!command) return null
        return {
            ...base,
            transportType: "stdio",
            command,
            args: stringArray(value.args),
            envVars: stringRecord(value.envVars),
        }
    }

    return null
}

export function parseMcpServerRecord(value: unknown): McpServerRecord | null {
    if (!isRecord(value) || value.v !== 1 || !Array.isArray(value.servers)) return null
    const updatedAt = stringField(value, "updatedAt")
    if (!updatedAt) return null
    return {
        v: 1,
        servers: value.servers.map(parseMcpServerItem).filter((server): server is McpServerItem => server !== null),
        updatedAt,
    }
}

export function createMcpServerRecord(servers: McpServerItem[] = [], updatedAt = new Date().toISOString()): McpServerRecord {
    return {
        v: 1,
        servers,
        updatedAt,
    }
}

function clone<T>(value: T): T {
    return globalThis.structuredClone(value)
}

export function createMcpServerStore(record: McpServerRecord = createMcpServerRecord(), onChange?: (servers: McpServerItem[]) => void): McpServerStore {
    const servers = observable.array<McpServerItem>(record.servers, { deep: false })
    const notify = () => onChange?.(servers.map((server) => clone(server)))

    const handle: McpServerArrayHandle<McpServerItem> = {
        all(): McpServerItem[] {
            return servers.map((server) => clone(server))
        },

        get(id: string): McpServerItem | undefined {
            const server = servers.find((item) => item.id === id)
            return server ? clone(server) : undefined
        },

        push(item: McpServerItem): void {
            servers.push(clone(item))
            notify()
        },

        update(id: string, recipe: (draft: McpServerItem) => void): void {
            const index = servers.findIndex((item) => item.id === id)
            if (index === -1) return
            const draft = clone(servers[index])
            recipe(draft)
            servers[index] = draft
            notify()
        },

        delete(id: string): void {
            const index = servers.findIndex((item) => item.id === id)
            if (index === -1) return
            servers.splice(index, 1)
            notify()
        },
    }

    makeObservable(handle, {
        push: action,
        update: action,
        delete: action,
    })

    return { servers: handle }
}
